<script setup lang="ts">
    import { computed, onMounted, watch, type Ref } from 'vue'
    import { useRoute, useRouter } from 'vue-router'

    import PortfolioNavigation from "@common/Components/PortfolioNavigation.vue"
    import Navigation from '@common/Navigation.vue'
    import PnlViewer from "@Quest/Pnls/Components/PnlViewer.vue"

    import { CurrencyDenomination } from '@common/types'
    import type { PnlTemplateReadModel } from '@Quest/Pnls/readmodels'
    import type { AlternativesViewDefReadModel, PnlPageReadModel } from '@Quest/Portfolios/readmodels'

    import { usePnlPageQueryV1 } from '@Quest/Portfolios/queries-vue-composables'

    const route = useRoute()
    const router = useRouter()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))

    const viewDefId = computed(() => route.params.viewDefId as string | null)
    const pnlTemplateId = computed<string | null>(() => (route.query.pnlTemplateId as string) ?? null)
    const denomination = computed<CurrencyDenomination>(() => (route.query.denomination as CurrencyDenomination) ?? CurrencyDenomination.Million)
    const applyBenefitsConfidenceFactor = computed<boolean>(() => route.query.risk === "true")
    const cagrVisible = computed(() => route.query.cagr === "true")

    const {
        runPnlPageQueryV1,
        modelPnlPageQueryV1
    } = usePnlPageQueryV1(workspaceId, tchProjectId, viewDefId, pnlTemplateId, applyBenefitsConfidenceFactor, denomination)

    const rm = modelPnlPageQueryV1 as Ref<PnlPageReadModel | null>
    const isWorkspaceMember = computed(() => rm.value?.IsWorkspaceMember ?? false)
    const pnlTemplates = computed<PnlTemplateReadModel[]>(() => rm.value?.Templates ?? [])
    const views = computed<AlternativesViewDefReadModel[]>(() => rm.value?.Lists ?? [])
    const pnlTemplateName = computed(() => rm.value?.PnlTemplateName ?? '')
    const viewDefName = computed(() => rm.value?.ViewDefName ?? '')

    const activeSharesCount = computed<number | null>(() => rm.value?.ActiveSharesCount ?? null)
    const shareId = computed<string>(() => rm.value?.ShareId ?? '')

    function setViewDefId(newViewDefId: string | null)
    {
        const basePath = `/portfolio/pnl/${workspaceId.value}/${tchProjectId.value}`

        const path = newViewDefId
            ? `${basePath}/${newViewDefId}`
            : basePath

        router.push({
            path,
            query: { ...route.query }
        })
    }

    function setDenomination(newDenomination: string)
    {
        router.replace({
            params: { ...route.params },
            query: { ...route.query, denomination: newDenomination }
        })
    }

    function setPnlTemplate(newId: string | null)
    {
        const query = { ...route.query }

        if (newId)
            query.pnlTemplateId = newId
        else
            delete query.pnlTemplateId

        router.replace({
            params: { ...route.params },
            query
        })
    }

    function toggleBenefitsConfidenceFactor()
    {
        router.replace({
            params: { ...route.params },
            query: {
                ...route.query,
                risk: String(!applyBenefitsConfidenceFactor.value)
            }
        })
    }

    function toggleCagr()
    {
        router.replace({
            params: { ...route.params },
            query: {
                ...route.query,
                cagr: String(!cagrVisible.value)
            }
        })
    }

    onMounted(() =>
    {
        document.title = "P&L | TransparentChoice"
        //runPnlPageQueryV1()
    })


    watch(() => route.fullPath, () => {
            runPnlPageQueryV1()
    },
    { immediate: true })

</script>

<template>
    <div v-if="rm">
        <PortfolioNavigation v-if="isWorkspaceMember" :workspaceId="workspaceId" :projectId="tchProjectId" />
        <Navigation v-else />

        <div class="container-fluid" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6 d-flex align-items-center">

                    <h1 v-if="!isWorkspaceMember" style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">
                        P&L: {{viewDefName}}
                    </h1>

                    <h1 v-if="isWorkspaceMember" style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">P&L:</h1>
                    <div v-if="isWorkspaceMember" class="dropdown ml-2">
                        <a class="btn btn-link" style="font-size: 2em; padding: 0;" data-toggle="dropdown">
                            {{viewDefName}} <i class="fa fa-caret-down" style="font-size: 0.7em; vertical-align: middle;"></i>
                        </a>
                        <div class="dropdown-menu dropdown-menu-scrollable" role="menu">
                            <a class="dropdown-item" @click="setViewDefId(null)">All</a>
                            <div class="dropdown-divider"></div>
                            <h6 class="dropdown-header">Custom lists</h6>
                            <a class="dropdown-item" v-for="v in views" :key="v.Id" @click="setViewDefId(v.Id)">{{v.Name}}</a>
                        </div>
                    </div>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <a v-if="isWorkspaceMember" class="btn btn-outline-secondary" style="padding: 0.7em 2em;"
                       :href="`/#/sharing/resource/PortfolioPnlsDataShareResourceProvider/${shareId}`" target="_blank">
                        Share <span v-if="activeSharesCount">({{activeSharesCount}})</span>
                    </a>
                </div>
            </div>
            <hr style="border-color: #bbb; margin: 20px 0 20px;" />

            <nav class="navbar navbar-light navbar-expand-md bg-light" role="navigation">

                <button type="button" class="btn mr-1" :class="applyBenefitsConfidenceFactor ? 'btn-danger' : 'btn-outline-secondary'" @click="toggleBenefitsConfidenceFactor()">
                    <i class="fa-regular" :class="applyBenefitsConfidenceFactor ? 'fa-square-check' : 'fa-square'"></i>
                    Risk adjusted benefits
                </button>

                <button type="button" class="btn mr-1" :class="cagrVisible ? 'btn-info' : 'btn-outline-secondary'" @click="toggleCagr">
                    <i class="fa-regular" :class="cagrVisible ? 'fa-square-check' : 'fa-square'"></i>
                    CAGR
                </button>

                <div class="dropdown mr-1">
                    <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown">
                        {{denomination}}s
                    </button>
                    <div class="dropdown-menu">
                        <a class="dropdown-item" @click="setDenomination('Million');">Millions</a>
                        <a class="dropdown-item" @click="setDenomination('Thousand');">Thousands</a>
                        <a class="dropdown-item" @click="setDenomination('Unit');">Units</a>
                    </div>
                </div>

                <div class="btn-group" v-if="pnlTemplates">
                    <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">{{pnlTemplateName}}</button>
                    <div class="dropdown-menu">
                        <h6 class="dropdown-header">P&L templates</h6>
                        <a class="dropdown-item" v-for="t in pnlTemplates" :key="t.Id" @click="setPnlTemplate(t.Id)">{{t.Name}}</a>
                    </div>
                </div>
            </nav>

            <PnlViewer :readModel="rm.Report" :cagrVisible="cagrVisible" />
        </div>
    </div>
</template>
